import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { AppShell } from '../components/Layout';
import { Banner } from '../components/ui';
import { useSession } from '../state/session';
import { POSTS } from '../content/posts';

/** What a sender gets. Every line here is something the product actually
 *  does today — features that exist only in the API say so, and features
 *  that do not exist are absent rather than softened.
 *
 *  One sentence each. Where a line needs an argument rather than a fact, the
 *  argument belongs on the page that already makes it — the comparisons in
 *  the blog — not in a card on a landing page nobody reaches the bottom of. */
const CAPABILITIES: [string, string, string][] = [
  ['Fields', 'Six field types',
   'Signature, initials, date, text, checkbox and radio, placed anywhere and '
   + 'assigned per recipient.'],
  ['Routing', 'Sequential or parallel',
   'Everyone at once, or one at a time so each signer sees the signature '
   + 'before theirs.'],
  ['Identity', 'Link or one-time passcode',
   'Decide per organization whether the link is enough, or a six-digit code '
   + 'to the recipient comes first.'],
  ['Follow-up', 'Reminders that cost nothing',
   'Nudge one recipient or everyone outstanding. Never consumes an allowance '
   + 'or a credit.'],
  ['Corrections', 'Withdraw and replace',
   'Pull an agreement back, or supersede it with a corrected version that '
   + 'stays linked to the original.'],
  ['Anywhere', 'Built for a phone first',
   'Preparing, placing fields and signing all work on a handset.'],
  ['Integrations', 'API, webhooks and embedded signing',
   'Send from your own system, and host signing or preparation inside your '
   + 'own product. Pro plan.'],
  ['Records', 'Exports you keep',
   'The signed PDF, completion report and evidence manifest, exportable at '
   + 'any time and verifiable without us.'],
];

const USE_CASES: [string, string][] = [
  ['Freelancers and studios',
   'Statements of work signed before the work starts, not after the invoice '
   + 'is disputed.'],
  ['Trades and field services',
   'Quotes and change orders signed on a phone at the property.'],
  ['Property and tenancy',
   'Tenancies and condition reports, where the question later is which '
   + 'version they signed.'],
  ['Hiring and onboarding',
   'Offers and policy acknowledgements, with a record of what each person '
   + 'was shown.'],
];

const SECURITY: [string, string][] = [
  ['Secrets are never stored',
   'Signing links, one-time codes, sessions and API keys exist only as '
   + 'hashes, and are redacted from logs and events.'],
  ['Uploads are parsed in isolation',
   'Every PDF is scanned, structurally validated and parsed in a separate '
   + 'process with its own memory, CPU and time limits.'],
  ['Tenancy is enforced on every read',
   'Organization scoping is applied in the service layer, not by hoping a '
   + 'document id stays secret.'],
  // Replaced the 2FA line that used to sit here. Administrative two-factor
  // is off in this deployment, and a security claim is published wherever it
  // is served — leaving the card up would have been a false one.
  ['A session does one job',
   'A signing session cannot download a completed agreement and a download '
   + 'session cannot sign. The purpose is checked on every use.'],
  ['Retrieval is reauthenticated',
   'An emailed link to a completed agreement is not enough — a fresh '
   + 'one-time code is required before the PDF is released.'],
  ['Cards never reach this application',
   'Card details are entered on the payment provider’s own page. We receive '
   + 'a reference and the last four digits, never the number.'],
];

/** Six questions, one paragraph each.
 *
 *  These used to run to two paragraphs apiece, which turned the foot of the
 *  page into an essay. The long answers were not deleted — each of them is a
 *  blog post already, and `more` links to it. The short answer here has to
 *  stand on its own, because most people will not follow the link. */
const FAQ: [string, string, string?, string?][] = [
  ['Is an electronic signature legally binding?',
   'In many jurisdictions, for most everyday agreements — with categories '
   + 'like wills and some property instruments commonly excluded. We cannot '
   + 'tell you whether yours is enforceable where you are, and a product that '
   + 'answers that in a sentence is overreaching. What we give you is '
   + 'evidence: the exact document, who acted, when, and how.',
   'are-electronic-signatures-legally-binding', 'What the law actually says'],
  ['What exactly is in the audit trail?',
   'Every event — opened, consented, authenticated, signed, declined, '
   + 'delivered, failed — with the actor, timestamp, authentication method and '
   + 'the digest of the revision they were looking at. It is append-only and '
   + 'hash-chained, so removing an entry is detectable as well as altering one.',
   'what-makes-an-audit-trail-worth-anything', 'What makes one worth anything'],
  ['Do the people signing need an account?',
   'No. They open the link, review the full agreement, consent, authenticate '
   + 'if you require it, and sign. No sign-up, no app, no download, and it '
   + 'works on a phone.'],
  ['What happens to my agreements if I stop paying?',
   'Cancelling stops new sends. You keep authenticated access to retrieve and '
   + 'export what you have already signed, and an export verifies on its own '
   + 'without this service. Retention is covered in the Terms of Service.',
   // Not a blog post — retention is worded in exactly one place, and this
   // sends people to it rather than restating it here.
   '', 'Read the retention terms'],
  ['What is the difference between credits and a plan?',
   'A plan is an annual subscription with a monthly allowance that resets. '
   + 'Credits are prepaid and never expire: $5 buys 50 credits and a send '
   + 'costs 10. Credits suit occasional sending; a plan suits regular sending '
   + 'or API access.',
   'pay-as-you-go-vs-subscription', 'Which one is cheaper for you'],
];

export function Home() {
  const { user } = useSession();
  const [pricing, setPricing] = useState<any>(null);

  useEffect(() => { api.get('/pricing').then(setPricing).catch(() => undefined); }, []);

  const credits = pricing?.credits;

  return (
    <AppShell
      nav={[
        { to: '/pricing', label: 'Pricing' },
        { to: '/blog', label: 'Blog' },
        { to: '/verify', label: 'Verify a document' },
      ]}
      actions={
        user
          ? <Link className="btn btn--primary btn--small" to="/app">Dashboard</Link>
          : (
            <>
              <Link className="btn btn--small btn--ghost" to="/login">Sign in</Link>
              <Link className="btn btn--primary btn--small" to="/register">Sign up</Link>
            </>
          )
      }
    >
      <section className="shell hero">
        <div style={{ maxWidth: '62ch' }}>
          <span className="hero__eyebrow">
            <span aria-hidden="true">◆</span> Prepare free — no card until you send
          </span>
          {/* Leads on detection rather than on evidence.
            *
            * "Signatures you can actually prove" was the better description of
            * what makes this product different, and the worse opening line: it
            * describes a benefit you only feel on the day something goes
            * wrong. Placing fields is the tedium everybody has already felt,
            * and the section immediately below demonstrates it on video — so
            * the hero makes a claim the next scroll pays off, instead of one
            * the reader has to take on trust. The evidence argument is still
            * made, under "What you can prove". */}
          <h1>Stop dragging boxes onto contracts.</h1>
          <p className="hero__lede">
            Upload a PDF and the fields are already placed — signature, date,
            initials, each on the right line and assigned to the right person.
            You add the email addresses.
          </p>
          <div className="btn-row btn-row--stack" style={{ marginTop: 26 }}>
            <Link className="btn btn--primary btn--large" to="/register">
              Create your account
            </Link>
            <Link className="btn btn--large" to="/prepare">
              Or try it without signing up
            </Link>
          </div>
          <p className="small muted" style={{ marginTop: 14 }}>
            Signing up is free and takes a moment. A card is required only to
            activate the three-day sending trial.{' '}
            <Link to="/verify">Verify a signed document</Link>
          </p>
        </div>
      </section>

      {/* Plain facts about the product, not claims about its popularity. */}
      <section className="shell" aria-label="At a glance">
        <div className="statstrip">
          {[
            // The whole relationship goes in one cell. A price stacked above a
            // per-send figure reads as the price *of* a send, which is five
            // times the truth.
            [`${credits ? credits.credits : 50} credits for `
             + `${credits ? credits.price_display.replace('USD ', '') : '$5.00'}`,
             `Each send costs ${credits ? credits.credits_per_send : 10} credits`
             + ` — ${credits ? credits.sends_per_pack : 5} sends a pack`],
            ['No expiry', 'Prepaid credits are never taken back'],
            // Retention is a real commitment but a poor headline: stated cold
            // it reads as "they hold my documents for seven years", which is
            // a fair reading. It belongs where someone is already asking what
            // happens to their records — the FAQ — not volunteered here.
            ['Checkable offline', 'Evidence verifies without an account, or us'],
            ['0 accounts', 'Signers never create one'],
          ].map(([value, label]) => (
            <div className="statstrip__item" key={label}>
              <span className="statstrip__value">{value}</span>
              <span className="statstrip__label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="band">
        <section className="shell section" aria-labelledby="autofields">
          <div className="grid grid--two" style={{ alignItems: 'start', gap: 28 }}>
            <div className="stack">
              <h2 id="autofields">It reads the signature block for you</h2>
              <p className="muted" style={{ maxWidth: '58ch' }}>
                Upload an agreement and the fields are already placed —
                signature, date, initials, each on the right line and assigned
                to the right person. It reads the printed names too, so the only
                thing left to type is an email address.
              </p>
              <ul className="plan-features" style={{ marginTop: 16 }}>
                <li>Every placement is a suggestion you can drag, resize or delete.</li>
                <li>No account needed to try it — upload one and see.</li>
              </ul>
              {/* Stated plainly rather than buried. A claim this page cannot
                  keep is worse than a weaker claim it can: detection is
                  reading, not magic, and a scan has nothing to read. */}
              <p className="small muted" style={{ marginTop: 16, maxWidth: '58ch' }}>
                It needs a PDF with real text in it. A scanned page has nothing
                to read, and an unusual layout may find less — you place those
                fields yourself.
              </p>
            </div>
            <figure className="shot" style={{ margin: 0 }}>
              {/* Plays on its own because the point of the feature is that you
                  do nothing — a video asking to be clicked would make that
                  argument badly. Muted and inline so it is allowed to, and
                  silent so there is nothing to miss with the sound off. */}
              <video
                poster="/detection.png"
                width={1200}
                height={590}
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                aria-label="Uploading an agreement: the fields appear already
                            placed and labelled for each signatory."
              >
                <source src="/detection.webm" type="video/webm" />
                <source src="/detection.mp4" type="video/mp4" />
                {/* Anything that will not play a video still sees the moment
                    that matters, because the poster is a frame of this video. */}
                <img
                  src="/detection.png"
                  srcSet="/detection@640.png 640w, /detection.png 1200w"
                  sizes="(min-width: 900px) 46vw, 92vw"
                  width={1200}
                  height={590}
                  alt="The field editor after uploading an agreement: a
                       two-column signature block with boxes already placed and
                       labelled Signature and Date for each of the two
                       signatories."
                />
              </video>
              <figcaption className="small muted" style={{ marginTop: 8 }}>
                A real upload. Nothing here was placed by hand.
              </figcaption>
            </figure>
          </div>
        </section>
      </div>

      <section className="shell section" aria-labelledby="how">
        <h2 id="how">How it works</h2>
        <div className="grid grid--cards" style={{ marginTop: 20 }}>
          {[
            ['1', 'Upload', 'Drop in a PDF. We read the signature block, place the fields and tell you who signs.'],
            ['2', 'Send', 'Check the names we found, add an email for each, adjust any field, and send.'],
            ['3', 'Sign', 'Recipients review the full terms, consent, verify their identity and sign — no account required.'],
            ['4', 'Keep', 'Export the final PDF, completion report and signed evidence manifest. The export verifies without us.'],
          ].map(([step, title, body]) => (
            <div className="card feature" key={step}>
              <span className="feature__icon" aria-hidden="true">{step}</span>
              <span className="feature__title">{title}</span>
              <span className="feature__body">{body}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="band">
        <section className="shell section" aria-labelledby="capabilities">
          <h2 id="capabilities">Everything a send needs</h2>
          <p className="muted" style={{ marginTop: 6, maxWidth: '62ch' }}>
            The only thing reserved for Pro is programmatic access.
          </p>
          <div className="grid grid--cards" style={{ marginTop: 20 }}>
            {CAPABILITIES.map(([tag, title, body]) => (
              <div className="card feature" key={title}>
                <span className="feature__icon" aria-hidden="true">
                  {tag.slice(0, 1)}
                </span>
                <span className="feature__title">{title}</span>
                <span className="feature__body">{body}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="shell section" aria-labelledby="evidence">
        <div className="grid grid--two" style={{ alignItems: 'start', gap: 28 }}>
          <div className="stack">
            <h2 id="evidence">What you can prove</h2>
            <p className="muted">
              Integrity here means something specific and checkable, not a badge.
            </p>
            <ul className="plan-features">
              <li>The exact bytes of the document each person saw, frozen at send.</li>
              <li>Who acted, when, and how they authenticated.</li>
              <li>Consent recorded with its version.</li>
              <li>An append-only, hash-chained record that detects deletion as
                  well as edits.</li>
              <li>An export that verifies offline, without a live account.</li>
            </ul>
          </div>
          <div className="stack">
            <Banner tone="info" title="What is not claimed">
              Agreements are finalized under an evidence-only assurance profile. No
              cryptographic PDF seal, externally trusted certificate or independent
              third-party timestamp is applied. A hash proves bytes are unchanged —
              it does not by itself establish identity, intent, or that an agreement
              is enforceable anywhere in particular.
            </Banner>
            <div className="card card--flat">
              <h3 className="card__title">Verify without trusting us</h3>
              <p className="small muted">
                Every export ships with the public evidence key and the commands
                to recompute each digest yourself.
              </p>
              <Link className="btn btn--small" to="/verify">Open the verifier</Link>
            </div>
          </div>
        </div>
      </section>

      <div className="band">
        <section className="shell section" aria-labelledby="signer">
          <div className="grid grid--two" style={{ alignItems: 'start', gap: 28 }}>
            <div className="stack">
              <h2 id="signer">What the other person sees</h2>
              <p className="muted">
                The part your customers actually judge you on. It asks for
                nothing it does not need.
              </p>
              <ul className="plan-features">
                <li>A link that opens the agreement — no account, no app, no
                    download.</li>
                <li>The full terms on screen before anything can be signed.</li>
                <li>A six-digit code to their inbox first, if you require it.</li>
                <li>Drawn or typed signatures, on a touchscreen or a trackpad.</li>
                <li>A copy of the completed agreement, released after a fresh
                    one-time code.</li>
              </ul>
            </div>
            <div className="stack">
              <h2 id="who">Who it is for</h2>
              <div className="stack-s" style={{ marginTop: 4 }}>
                {USE_CASES.map(([who, what]) => (
                  <div className="usecase" key={who}>
                    <span className="usecase__who">{who}</span>
                    <span className="usecase__what">{what}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* No organization-seal section.
          *
          * The seal is built but unapplied -- Phase 2 depends on certificate
          * issuance and a timestamp authority still being procured. A plan
          * page can honestly say "included, not applied yet", but the cleaner
          * answer while that is true is not to sell it at all: no claim needs
          * no caveat, and when sealing goes live this becomes a feature to
          * add rather than a disclaimer to retract.
          *
          * This is about the Pro *entitlement* only. What artifacts report is
          * a different question and has not changed: the completion report,
          * the manifest and the verifier still say the PDF seal, certificate
          * trust and independent timestamp are "not applied", and the
          * assurance notice in the evidence section below still says so too.
          * Those are statements about what an agreement actually carries, and
          * removing them would be widening an assurance claim. */}

      <section className="shell section" aria-labelledby="security">
        <h2 id="security">Security that is specific</h2>
        <p className="muted" style={{ marginTop: 6, maxWidth: '62ch' }}>
          Not a padlock and the words “bank-grade” — controls you can check in
          the behaviour of the product.
        </p>
        <div className="grid grid--cards" style={{ marginTop: 20 }}>
          {SECURITY.map(([title, body]) => (
            <div className="card feature" key={title}>
              <span className="feature__title">{title}</span>
              <span className="feature__body">{body}</span>
            </div>
          ))}
        </div>
      </section>

      {pricing && (
        <div className="band">
          <section className="shell section" aria-labelledby="plans">
            <h2 id="plans">Pay by the year, or by the send</h2>
            <p className="muted" style={{ marginTop: 6, maxWidth: '62ch' }}>
              Two ways to pay and one product.
            </p>
            <div className="grid grid--cards" style={{ marginTop: 20 }}>
              {credits && (
                <div className="card pricecard" key="credits">
                  <div>
                    <div className="row row--between">
                      <h3 style={{ margin: 0 }}>{credits.plan_name}</h3>
                      <span className="pill">No subscription</span>
                    </div>
                    <div className="price">
                      {credits.price_display.replace('USD ', '')}
                      <span className="price__unit"> / {credits.credits} credits</span>
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li>
                      {credits.credits_per_send} credits a send —{' '}
                      {credits.sends_per_pack} sends per pack
                    </li>
                    <li><strong>Credits never expire</strong></li>
                    <li>Up to 10 signers per request</li>
                    <li>Unlimited self-signing</li>
                    <li>Evidence export at any time</li>
                    <li className="no">No API access</li>
                  </ul>
                  <span className="spacer" />
                  <Link className="btn btn--block" to="/register">Pay as you go</Link>
                </div>
              )}
              {pricing.plans.map((plan: any) => (
                <div className={`card pricecard${plan.plan_id === 'pro' ? ' pricecard--featured' : ''}`}
                     key={plan.plan_id}>
                  <div>
                    <div className="row row--between">
                      <h3 style={{ margin: 0 }}>{plan.plan_name}</h3>
                      {plan.plan_id === 'pro' && <span className="pill pill--brand">API included</span>}
                    </div>
                    <div className="price">
                      {plan.price_display.replace('USD ', '')}
                      <span className="price__unit"> / year</span>
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li>{plan.monthly_requests} signature requests per month</li>
                    <li>Up to 10 signers per request</li>
                    <li>Unlimited self-signing</li>
                    <li>Evidence export at any time</li>
                    <li className={plan.api_access ? '' : 'no'}>
                      {plan.api_access ? 'API access and webhooks' : 'No API access'}
                    </li>
                  </ul>
                  <span className="spacer" />
                  <Link className={`btn btn--block${plan.plan_id === 'pro' ? ' btn--primary' : ''}`}
                        to="/register">
                    Start {plan.trial_days}-day trial
                  </Link>
                </div>
              ))}
            </div>
            <p className="small muted" style={{ marginTop: 14 }}>
              Trials require a card and include {pricing.plans[0].trial_requests} signature
              requests. Billing begins when the trial expires unless you cancel first.
              {' '}<Link to="/pricing">See the full allowance rules</Link>.
            </p>
          </section>
        </div>
      )}

      <section className="shell section" aria-labelledby="faq">
        <h2 id="faq">Questions worth a straight answer</h2>
        <div className="faq" style={{ marginTop: 20 }}>
          {FAQ.map(([question, answer, slug, more]) => (
            <details className="faq__item" key={question}>
              <summary className="faq__q">{question}</summary>
              <p className="faq__a">{answer}</p>
              {more && (
                <p className="faq__a">
                  <Link className="small" to={slug ? `/blog/${slug}` : '/terms#retention'}>
                    {more} →
                  </Link>
                </p>
              )}
            </details>
          ))}
        </div>
      </section>

      <div className="band">
        <section className="shell section" aria-labelledby="writing">
          <div className="row row--between" style={{ alignItems: 'baseline' }}>
            <h2 id="writing" style={{ margin: 0 }}>From the blog</h2>
            <Link className="small" to="/blog">All writing →</Link>
          </div>
          <div className="grid grid--cards" style={{ marginTop: 20 }}>
            {POSTS.slice(0, 3).map((post) => (
              <Link className="card postcard" to={`/blog/${post.slug}`} key={post.slug}>
                <span className="postcard__meta">
                  <span className="pill pill--brand">{post.category}</span>
                  <span className="small muted">{post.minutes} min read</span>
                </span>
                <span className="postcard__title">{post.title}</span>
                <span className="postcard__lede">{post.description}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="shell section">
        <div className="cta">
          <h2>Start with the part that costs nothing</h2>
          <p>
            Upload a PDF, place the fields and see the whole signing experience
            before you decide. A card is needed only when you send.
          </p>
          <div className="btn-row btn-row--stack" style={{ justifyContent: 'center' }}>
            <Link className="btn btn--primary btn--large" to="/register">
              Create your account
            </Link>
            <Link className="btn btn--large" to="/pricing">Compare pricing</Link>
          </div>
          <p className="small muted" style={{ marginTop: 12 }}>
            Prefer to look first?{' '}
            <Link to="/prepare">Prepare an agreement without an account.</Link>
          </p>
        </div>
      </section>
    </AppShell>
  );
}
